import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useLazyQuery } from '@apollo/client/react';
import { ClockCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Card, Input, Timeline, Tooltip } from 'antd';
import { useDebounce } from 'react-use';
import styled from '@emotion/styled';

import QueryBoundary from 'components/QueryBoundary/QueryBoundary';
import SectionMessage from 'components/SectionMessage/SectionMessage';
import useAnalytics from 'hooks/useAnalytics';
import useScrollLimit from 'hooks/useScrollLimit';
import {
  ACCOUNT_HISTORY_APPLICATION,
  ACCOUNT_HISTORY_CUSTOMER,
} from 'queries/accountHistory';
import { ANALYTICS_EVENTS, STATUS } from 'utilities/constants';
import { SIZE } from 'utilities/constants';

import AccountHistoryWorker from './accountHistoryWorker?worker';
import { EVENT_TYPE, TYPE_FILTER } from './accountHistoryUtilities';

import CommunicationEvent from './CommunicationEvent';
import Event from './Event';

import MultiSelectDropdown from 'components/MultiSelectDropdown/MultiSelectDropdown';

const ExtraTabContainer = styled.div`
  display: flex;
  margin-top: 5px;
  margin-bottom: 5px;
`;

const SCROLL_INTERVAL = 300;

const typeFilterOptions = [
  {
    label: 'Digital Wallet',
    key: TYPE_FILTER.DIGITAL_WALLET,
  },
  {
    label: 'Email',
    key: TYPE_FILTER.EMAIL,
  },
  {
    label: 'Fraud Alert',
    key: TYPE_FILTER.FRAUD_ALERT,
  },
];

const formatEvents = (events, textFilter) => {
  const items = [];

  events.forEach((event) => {
    let content;
    const { __typename: type } = event;
    switch (type) {
      case EVENT_TYPE.COMMUNICATIONS:
        content = (
          <CommunicationEvent
            event={event}
            highlight={textFilter}
          ></CommunicationEvent>
        );
        break;
      case EVENT_TYPE.AUTOMATED_UPDATE:
      case EVENT_TYPE.MEMO:
      case EVENT_TYPE.DIGITAL_WALLET_EVENT:
        content = <Event event={event} highlight={textFilter}></Event>;
        break;
      default:
        content = null;
    }
    items.push({
      icon: <ClockCircleOutlined />,
      content,
    });
  });
  return items;
};

const AccountHistory = () => {
  const bodyHeight = '440px';

  const { trackEvent } = useAnalytics();
  const { customerId, applicationId } = useParams();

  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [processingEvents, setProcessingEvents] = useState(false);

  const [typeFilters, setTypeFilters] = useState('');

  const [textFilter, setTextFilter] = useState('');
  const [debouncedTextFilter, setDebouncedTextFilter] = useState();
  useDebounce(
    () => {
      setDebouncedTextFilter(textFilter);
    },
    500,
    [textFilter],
  );

  const { onScroll } = useScrollLimit(SCROLL_INTERVAL, (scrollLimit) =>
    trackEvent(ANALYTICS_EVENTS.AUTO_EVENTS_SCROLL_LIMIT_HIT, {
      limit: Math.floor(scrollLimit / SCROLL_INTERVAL),
    }),
  );

  const [accountHistoryWorker, postToWorker] = useMemo(() => {
    const accountHistoryWorker = new AccountHistoryWorker();

    const postToWorker = ({
      queryData,
      events = [],
      textFilter = '',
      typeFilters = '',
    }) => {
      accountHistoryWorker.postMessage({
        queryData: queryData ? JSON.stringify(queryData) : undefined,
        events: JSON.stringify(events),
        textFilter,
        typeFilters,
      });
    };

    return [accountHistoryWorker, postToWorker];
  }, []);

  accountHistoryWorker.onmessage = useCallback((message) => {
    const workerResp = JSON.parse(message.data);
    const { events, filteredEvents, textFilter } = workerResp;
    setEvents(events);
    setFilteredEvents(formatEvents(filteredEvents, textFilter));
    setProcessingEvents(false);
  }, []);

  const processEvents = (textFilter, typeFilters, events) => {
    setProcessingEvents(true);
    postToWorker({ events, textFilter, typeFilters });
  };

  const processQueryData = (queryData = {}) => {
    setProcessingEvents(true);
    postToWorker({ queryData, textFilter, typeFilters });
  };

  const [getUpdatesForApplication, accountHistoryApplicationQuery] =
    useLazyQuery(ACCOUNT_HISTORY_APPLICATION, {
      notifyOnNetworkStatusChange: true,
    });

  const [getUpdatesForCustomer, accountHistoryCustomerQuery] = useLazyQuery(
    ACCOUNT_HISTORY_CUSTOMER,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-first',
    },
  );

  const getUpdates = async () => {
    setProcessingEvents(true);
    try {
      if (applicationId) {
        await getUpdatesForApplication({
          variables: { applicationId },
        });
      } else if (customerId) {
        await getUpdatesForCustomer({
          variables: { customerId },
        });
      }
    } catch (error) {
      setProcessingEvents(false);
    }
  };

  const reload = async () => {
    setProcessingEvents(true);
    try {
      if (applicationId) {
        await accountHistoryApplicationQuery.refetch();
      } else if (customerId) {
        await accountHistoryCustomerQuery.refetch();
      }
    } catch (error) {
      setProcessingEvents(false);
    }
  };

  useEffect(() => {
    if (applicationId && accountHistoryApplicationQuery.data) {
      processQueryData(accountHistoryApplicationQuery.data);
    } else if (customerId && accountHistoryCustomerQuery.data) {
      processQueryData(accountHistoryCustomerQuery.data);
    }
  }, [
    applicationId,
    customerId,
    accountHistoryApplicationQuery.data,
    accountHistoryCustomerQuery.data,
  ]);

  useEffect(() => {
    setTextFilter('');
    getUpdates();
  }, [customerId, applicationId]);

  useEffect(() => {
    if (events && events.length) {
      processEvents(debouncedTextFilter, typeFilters, events);
    }
  }, [debouncedTextFilter, typeFilters]);

  const activeQuery = applicationId
    ? accountHistoryApplicationQuery
    : accountHistoryCustomerQuery;

  const filterActive = textFilter || typeFilters.length;

  return (
    <Card
      style={{ minHeight: '500px', paddingBottom: '1rem' }}
      styles={{
        body: {
          overflow: 'auto',
          height: bodyHeight, //specific height to sit inline with Interactions
          padding: '1rem',
        },
      }}
      size="small"
      title="Account History"
      extra={
        <ExtraTabContainer>
          <Input
            placeholder="Search..."
            allowClear
            data-testid="text-filter"
            type="text"
            value={textFilter}
            onChange={(event) => {
              setTextFilter(event.target.value);
            }}
          />
          <MultiSelectDropdown
            items={typeFilterOptions}
            onChange={(filters) => setTypeFilters(filters.join(','))}
            triggerProps={{
              'data-testid': 'type-filter',
            }}
          />
          <Tooltip
            title={
              textFilter.length > 0 ? 'Clear filter to refresh' : 'Refresh'
            }
          >
            <Button
              data-testid="reload-auto-notes"
              type="link"
              aria-label="Refresh"
              icon={<ReloadOutlined />}
              onClick={reload}
              // We should not need to disable refresh if a filter is active. Check implementation and fix this.
              disabled={textFilter.length > 0}
            />
          </Tooltip>
        </ExtraTabContainer>
      }
    >
      <QueryBoundary
        messageHeight={bodyHeight}
        errorMessage="Failed to load Auto Events."
        query={activeQuery}
        loadingOverride={activeQuery.loading || processingEvents}
        skeleton={{
          title: false,
          paragraph: { rows: 13 },
        }}
      >
        {filteredEvents.length ? (
          <Timeline
            mode="start"
            data-testid="auto-notes-timeline"
            onScroll={onScroll}
            items={filteredEvents}
          />
        ) : (
          <SectionMessage
            size={SIZE.SM}
            status={filterActive ? STATUS.PENDING : STATUS.WARNING}
            style={{ height: '100%' }}
            text={
              filterActive
                ? 'Filter settings returned no results'
                : 'No account history'
            }
          />
        )}
      </QueryBoundary>
    </Card>
  );
};

export default AccountHistory;
